import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { MarkupExistsError, MarkupService, RedlineBody, RedlineSlot } from './markup.service';
import { AuthService } from './auth.service';
import { CommonService } from './common.service';
import { dataUrlToFile } from './map-capture.service';
import { Markup } from '../models/markup';
import { MapFeatureLink, MapFeatureLinkSheetPoint } from '../models/map-feature-link';

/** Strokes drawn in the picker on a sheet point whose link is not saved yet (spec §12 slice 3, R16). */
export interface HeldSheetMarkup {
  /** The unsaved link's `client_key` (review #1): the save echoes it on the link it created, and only that link matches */
  key: string;
  revisionUuid: string;
  /** Null for a markup-only location (no pin) */
  point: MapFeatureLinkSheetPoint | null;
  /** The revision's set attachment and page: the markup row's slot. */
  attachmentUuid: string;
  pageNumber: number;
  /** For the failure message. */
  sheetNumber: string | null;
  /** The revision's label, for the "already has marks" toast. */
  revisionLabel?: string | null;
  /** Where the toast's Open goes (the project's Drawings tab). */
  projectId?: string | null;
  body: RedlineBody;
  /** 7.2: the record's markup as an as-built mark (a real one, tagged to the link); a note otherwise */
  kind?: 'as_built' | null;
  /** Strokes only, as a transparent PNG: its upload is what bakes the strokes into the link's snapshot. */
  previewDataUrl: string;
}

interface QueueEntry extends HeldSheetMarkup {
  accountId: string | null;
  expiresAt: number;
}

/** Held strokes outlive a slow form, not a session: past this they are dropped unposted. */
export const SHEET_MARKUP_TTL_MS = 30 * 60 * 1000;
/** Points round-trip through JSON and Postgres jsonb; anything closer than this is the same point. */
const MAX_DEPTH = 6;

/** A new `client_key` for an unsaved link that is about to hold strokes */
export function newClientKey(): string {
  return uuidv4();
}

/**
 * A record's redline on a sheet is one shared markup row tagged with its link (`link_uuid`), and the link exists only
 * once the record is saved. The picker holds the strokes here under the unsaved link's `client_key`; the record's save
 * success hands the api response to `flush`, which finds the saved link carrying that key and posts the row, then its
 * preview (the preview upload re-renders the link's snapshot). Never by revision or point: two pinless rows on one
 * sheet, or a line saved earlier on the same point, would take each other's strokes (review #1). Root service: keyed
 * to the account, like every root cache.
 */
@Injectable({ providedIn: 'root' })
export class SheetMarkupQueueService {
  private markupService = inject(MarkupService);
  private auth = inject(AuthService);
  private common = inject(CommonService);
  private snack = inject(MatSnackBar);
  private router = inject(Router);
  private entries: QueueEntry[] = [];
  /** Overridable clock (specs). */
  now = (): number => Date.now();

  /** Holds (or replaces) the strokes of one unsaved link; another link's strokes on the same sheet are untouched */
  hold(entry: HeldSheetMarkup): void {
    if (!entry.key) return;
    this.release(entry.key);
    this.entries.push({ ...entry, accountId: this.accountId(), expiresAt: this.now() + SHEET_MARKUP_TTL_MS });
  }

  /** Held strokes under this key, for the picker to re-open. */
  get(key: string | null | undefined): HeldSheetMarkup | null {
    if (!key) return null;
    this.prune();
    return this.entries.find(entry => entry.key === key) || null;
  }

  /** The strokes held for this unsaved link (a saved link holds none) */
  heldFor(link: Pick<MapFeatureLink, 'uuid' | 'client_key'> | null | undefined): HeldSheetMarkup | null {
    return link && !link.uuid ? this.get(link.client_key) : null;
  }

  release(key: string | null | undefined): void {
    if (!key) return;
    this.entries = this.entries.filter(entry => entry.key !== key);
  }

  pending(): number {
    this.prune();
    return this.entries.length;
  }

  /**
   * After a record save: posts the held strokes of every saved sheet link in `response` (any depth: a report's line
   * arrays, an issue) that carries the strokes' key. Resolves to the number of rows posted. A failure is reported once
   * and not retried. Two different links echoing one key would be a guess: nothing is posted, and the user is told.
   */
  async flush(response: unknown): Promise<number> {
    this.prune();
    if (!this.entries.length) return 0;
    const links = savedSheetLinks(response);
    let posted = 0;
    for (const entry of [...this.entries]) {
      const uuids = [...new Set(links.filter(link => link.clientKey === entry.key).map(link => link.uuid))];
      if (!uuids.length) {
        // Saved on its revision under another key (or none): this save took the record's link, but not under the
        // strokes' key, so they can never post. Said once, never dropped silently. Another record's strokes (no link on
        // their revision in this response) wait for their own save.
        if (links.some(link => link.revisionUuid === entry.revisionUuid)) {
          this.entries = this.entries.filter(item => item !== entry);
          console.warn('Held sheet markup: the save echoed no link with its key', entry.key);
          this.notSaved(entry);
        }
        continue;
      }
      this.entries = this.entries.filter(item => item !== entry);
      if (uuids.length > 1) {
        console.warn('Held sheet markup matched more than one saved link; not posted', entry.key);
        this.notSaved(entry);
        continue;
      }
      // One row per linked record in a save (usually one), posted in order.
      if (await this.post(entry, uuids[0])) posted++;
    }
    return posted;
  }

  private async post(entry: QueueEntry, linkUuid: string): Promise<boolean> {
    const slot: RedlineSlot = { attachment_id: entry.attachmentUuid, page_number: entry.pageNumber, visibility: 'shared', link_uuid: linkUuid };
    let row: Markup;
    try {
      // 7.2: held as-built strokes settle as an as_built row tagged to the new link (open, in the review queue)
      row = await firstValueFrom(entry.kind === 'as_built'
        ? this.markupService.createAsBuilt(entry.body, { attachment_id: slot.attachment_id, page_number: slot.page_number, link_uuid: linkUuid }, uuidv4())
        : this.markupService.saveRedline(null, entry.body, slot));
    } catch (error) {
      if (error instanceof MarkupExistsError) {
        // The link already holds marks (another device, or a link kept in place): never write over them (W26).
        this.alreadyMarked(entry);
        return false;
      }
      console.error('Failed to save the held sheet markup:', error);
      this.notSaved(entry);
      return false;
    }
    try {
      await firstValueFrom(this.markupService.uploadPreview(row.id, dataUrlToFile(entry.previewDataUrl, `markup-${row.id}.png`)));
    } catch (error) {
      // The row is saved; only the snapshot misses the strokes until the next markup save.
      console.error('Failed to upload the held sheet markup preview:', error);
    }
    return true;
  }

  private notSaved(entry: QueueEntry): void {
    this.common.notification(`Your markup on ${entry.sheetNumber || 'the sheet'} could not be saved. Mark it up again from the sheet.`, 'error');
  }

  /** The held strokes are dropped; the toast says where the record's marks are and opens that revision. */
  private alreadyMarked(entry: QueueEntry): void {
    const sheet = [entry.sheetNumber || 'the sheet', entry.sheetNumber && entry.revisionLabel ? `Rev ${entry.revisionLabel}` : ''].filter(Boolean).join(' ');
    const ref = this.snack.open(`This record already has marks on ${sheet}. Open the sheet to edit them.`, entry.projectId ? 'Open' : 'Dismiss',
      { duration: 10000, verticalPosition: 'top', horizontalPosition: 'center' });
    if (!entry.projectId) return;
    ref.onAction().subscribe(() => {
      void this.router.navigate(['/projects', entry.projectId], { queryParams: { tab: 'drawings', revision: entry.revisionUuid } });
    });
  }

  private prune(): void {
    const now = this.now();
    const account = this.accountId();
    this.entries = this.entries.filter(entry => entry.expiresAt > now && entry.accountId === account);
  }

  private accountId(): string | null {
    return this.auth.getCurrentUser()?.id ?? null;
  }
}

interface SavedSheetLink { uuid: string; revisionUuid: string; clientKey: string | null }

/** Every serialized `map_feature_link` with a uuid and a sheet target in an api response, with its `client_key` */
function savedSheetLinks(response: unknown): SavedSheetLink[] {
  const found: SavedSheetLink[] = [];
  const walk = (node: unknown, depth: number) => {
    if (!node || typeof node !== 'object' || depth > MAX_DEPTH) return;
    if (Array.isArray(node)) { node.forEach(item => walk(item, depth + 1)); return; }
    const record = node as Record<string, unknown>;
    const link = record.map_feature_link as { uuid?: string; client_key?: string | null; sheet?: { revision_uuid?: string } } | null;
    if (link?.uuid && link.sheet?.revision_uuid) {
      found.push({ uuid: link.uuid, revisionUuid: link.sheet.revision_uuid, clientKey: typeof link.client_key === 'string' && link.client_key ? link.client_key : null });
    }
    for (const value of Object.values(record)) walk(value, depth + 1);
  };
  walk(response, 0);
  return found;
}

/** Held strokes re-opened in the editor, with their kind (7.2) */
export function heldMarkup(held: HeldSheetMarkup): Markup {
  return new Markup({ ...held.body, ...(held.kind === 'as_built' ? { kind: 'as_built' } : {}) });
}
